import {
  IActiveStatus,
  IChatUnread,
  ISendMessage,
  IUpdateMessage,
  IMessage,
  IGetMessages,
  ChatService,
  IChatRoomSummary,
  IGetMessagesResponse,
  ChatRoom,
  UserOnlineStatus,
  ChatSocketService
} from '@actimi/core-migration';
// Need to use the React-specific entry point to allow generating React hooks
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { chatSocket } from 'src/utils/socket';
import axiosInt from '../utils/axios';
import * as _ from 'lodash';

export enum ChatApiTags {
  Messages = 'Messages',
  ChatRooms = 'ChatRooms',
  ChatRoomsUnread = 'ChatRoomsUnread',
  ActiveStatus = 'ActiveStatus',
  InitializeChatRoom = 'InitializeChatRoom'
}

export const chatService = new ChatService(chatSocket);
export const chatSocketService = new ChatSocketService(chatSocket);

// Define a service using a base URL and expected endpoints
export const chatApi = createApi({
  reducerPath: 'chatApi',
  baseQuery: fetchBaseQuery({
    baseUrl: axiosInt.defaults.baseURL,
    prepareHeaders(headers) {
      const auth_token = axiosInt.defaults.headers.Authorization;
      if (auth_token) {
        headers.set('Authorization', auth_token);
      }
      // If we have a token set in state, let's assume that we should be passing it.
      return headers;
    },
    credentials: 'include'
  }),
  tagTypes: [
    ChatApiTags.Messages,
    ChatApiTags.ChatRooms,
    ChatApiTags.ChatRoomsUnread,
    ChatApiTags.ActiveStatus
  ],
  endpoints: (builder) => ({
    getMessages: builder.query<IGetMessagesResponse, IGetMessages>({
      keepUnusedDataFor: 1,
      queryFn: async (params, queryApi, extraOptions, baseQuery) => {
        const data = await chatService.getMessages(params);
        return { data };
      },
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }
      ) {
        // wait for the initial query to resolve before proceeding
        await cacheDataLoaded;

        // when data is received from the socket connection to the server, update our query result with the received message
        const listener = (event: { message: IMessage }) => {
          const { message } = event;
          updateCachedData((draft) => {
            if (!draft.chatId || draft.chatId !== message.chatRoom.id) {
              return;
            }
            const received = draft.messages.find(
              (oldMessage) => oldMessage.id === message.id
            );
            if (received) {
              Object.assign(received, message);
            } else if (draft.messages === undefined) {
              draft.messages = [message];
            } else if (
              draft.messages.length === 0 ||
              message.meta.createdAt > draft.messages[0].meta.createdAt
            ) {
              draft.messages.unshift(message);
            } else {
              console.log(
                'message received for the current chat but neither in cache or new',
                message
              );
            }
            dispatch(
              chatApi.util.invalidateTags([
                {
                  type: ChatApiTags.ChatRoomsUnread,
                  id: message.chatRoom.id
                },
                { type: ChatApiTags.ChatRoomsUnread, id: 'LIST' },
                {
                  type: ChatApiTags.ChatRooms,
                  id: message.to.id
                },
                {
                  type: ChatApiTags.ChatRooms,
                  id: message.from.id
                }
              ])
            );
          });
        };

        chatSocket.on(`new-message`, listener);
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;
        console.log('cacheEntryRemoved', arg.chatId);
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        chatSocket.off(`new-message`, listener);
      },
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.messages.map(
                ({ id }) => ({ type: ChatApiTags.Messages, id } as const)
              ),
              { type: ChatApiTags.Messages, id: 'LIST' }
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: ChatApiTags.Messages, id: 'LIST' }` is invalidated
            [{ type: ChatApiTags.Messages, id: 'LIST' }]
    }),
    getChatUnread: builder.query<
      IChatUnread[],
      { chatId: string[]; userId: string }
    >({
      queryFn: async (params) => {
        const data = await chatService.getUnreadChatMessages(params);
        return { data };
      },
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(
                ({ chatId }) =>
                  ({ type: ChatApiTags.ChatRoomsUnread, id: chatId } as const)
              ),
              { type: ChatApiTags.ChatRoomsUnread, id: 'LIST' }
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: ChatApiTags.ChatRooms, id: 'LIST' }` is invalidated
            [{ type: ChatApiTags.ChatRoomsUnread, id: 'LIST' }]
    }),
    getActiveStatus: builder.query<IActiveStatus[], { userId: string[] }>({
      queryFn: (params) => {
        return { data: [] };
      },
      // TODO: Use UserOnlineStatus
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }
      ) {
        // create a websocket connection when the cache subscription starts
        // wait for the initial query to resolve before proceeding
        await cacheDataLoaded;

        // when data is received from the socket connection to the server, update our query result with the received message
        const listener = (event: IActiveStatus) => {
          const data: IActiveStatus = event;
          updateCachedData((draft) => {
            const index = draft.findIndex((x) => x.userId === data.userId);
            if (index > -1) {
              draft[index] = data;
            }
          });
        };
        if (!chatSocket.hasListeners(`active-status-changed`)) {
          chatSocket.on(`active-status-changed`, listener);
        }
        chatSocket.emit(
          'listen-active-status',
          arg.userId.map((userId) => ({ userId }))
        );
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        chatSocket.off(`active-status-changed`);
      },
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(
                ({ id }) => ({ type: ChatApiTags.ActiveStatus, id } as const)
              ),
              { type: ChatApiTags.ActiveStatus, id: 'LIST' }
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: ChatApiTags.ChatRooms, id: 'LIST' }` is invalidated
            [{ type: ChatApiTags.ActiveStatus, id: 'LIST' }]
    }),
    getChat: builder.query<
      IChatRoomSummary[],
      { userId: string; organizationId: string }
    >({
      keepUnusedDataFor: 1,
      queryFn: async ({ userId, organizationId }) => {
        const data = await chatService.getPractitionerChatRooms(
          userId,
          organizationId
        );
        return {
          data: _.sortBy(
            data,
            (s) => s.chatRoom?.meta?.lastUpdated ?? '-1'
          ).reverse()
        };
      },
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, dispatch }
      ) {
        await cacheDataLoaded;

        const activeStatusListener = (data: UserOnlineStatus) => {
          updateCachedData((draft) => {
            const index = draft.findIndex((x) => x.userId === data?.user?.id);
            if (index > -1) {
              draft[index].onlineStatus = data;
              // Reset is typing to false instead expecting it from other chat client to inform.
              if (!data?.isOnline) {
                draft[index].isTyping = false;
              }
              // Object.assign({...draft[index], onlineStatus: data});
            }
          });
        };

        const chatRoomListener = (event: { chatRoom: ChatRoom }) => {
          const { chatRoom } = event;
          const userId = chatRoom.members.find(
            ({ id }) => id !== arg.userId
          )?.id;
          if (!userId) return;
          updateCachedData((draft) => {
            const foundChatIdx = draft.findIndex(
              (chat) => chat.userId === userId
            );
            if (foundChatIdx > -1) {
              draft[foundChatIdx].chatRoom = chatRoom;

              // TODO: Invalidating the chat room because we are not receiving last message, so fetch the new last message here maybe?
              if (
                draft[foundChatIdx].chatRoom?.lastMessage?.id !==
                chatRoom?.lastMessage?.id
              ) {
                dispatch(
                  chatApi.util.invalidateTags([
                    {
                      type: ChatApiTags.ChatRooms,
                      id: userId
                    }
                  ])
                );
              }
            }
          });
        };

        const isTypingListener = (event: {
          chatId: string;
          isTyping: boolean;
        }) => {
          if (typeof event !== 'object') return;
          const { chatId, isTyping } = event;
          if (!chatId) return;
          updateCachedData((draft) => {
            const index = draft.findIndex((x) => x.chatRoom?.id === chatId);
            if (index > -1) {
              draft[index].isTyping = !!isTyping;
              // Object.assign({...draft[index], onlineStatus: data});
            }
          });
        };

        chatSocket.on('active-status-changed', activeStatusListener);
        chatSocket.on(`new-chat-room`, chatRoomListener);
        chatSocket.on('is-typing', isTypingListener);

        await cacheEntryRemoved;
        chatSocket.off(`active-status-changed`, activeStatusListener);
        chatSocket.off(`new-chat-room`, chatRoomListener);
        chatSocket.off('is-typing', isTypingListener);
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
      },
      providesTags: (result, error, arg) =>
        // is result available?
        result
          ? // successful query
            [
              ...result.map(
                ({ userId }) =>
                  ({ type: ChatApiTags.ChatRooms, id: userId } as const)
              ),
              { type: ChatApiTags.ChatRooms, id: 'LIST' }
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: ChatApiTags.ChatRooms, id: 'LIST' }` is invalidated
            [{ type: ChatApiTags.ChatRooms, id: 'LIST' }]
    }),
    sendMessage: builder.mutation<undefined, ISendMessage>({
      queryFn: async (params) => {
        await chatService.sendMessage(params);
        return { data: undefined };
      },
      // Invalidates ChatRooms and Messages cache when the mutation is successful
      invalidatesTags: (res: IMessage) => {
        return [
          { type: ChatApiTags.ChatRooms, id: res.from.id || 'LIST' },
          { type: ChatApiTags.ChatRooms, id: res.to.id || 'LIST' },
          { type: ChatApiTags.Messages, id: 'LIST' }
        ];
      }
    }),
    updateMessage: builder.mutation<
      undefined,
      IUpdateMessage & { userId: string }
    >({
      queryFn: async ({ userId, messageId, status }) => {
        await chatService.updateMessageStatus(userId, messageId, status);
        return { data: undefined };
      },
      // Invalidates ChatRooms and Messages cache when the mutation is successful
      invalidatesTags: (res: IMessage) => {
        return [
          { type: ChatApiTags.ChatRoomsUnread, id: res.chatRoom.id },
          { type: ChatApiTags.ChatRoomsUnread, id: 'LIST' }
        ];
      }
    }),
    updateMessagesToSeen: builder.mutation<
      undefined,
      { userId: string; chatId: string }
    >({
      queryFn: async ({ userId, chatId }) => {
        await chatService.updateAllMessagesToSeen(userId, chatId);
        return { data: undefined };
      },
      invalidatesTags: (res, err, args) => [
        { type: ChatApiTags.ChatRoomsUnread, id: args.userId }
      ]
    }),
    createChatRoom: builder.mutation<
      ChatRoom | null,
      {
        userId?: string;
        meId: string;
        patientId?: string;
        organizationId: string;
      }
    >({
      queryFn: async (params, api) => {
        let userId = params.userId;
        if (!params.userId && !params.patientId)
          throw new Error('userId or patientId is required!');
        if (params.patientId && !userId) {
          const { id } = await chatService.getUserIdFromPatientId({
            organizationId: params.organizationId,
            patientId: params.patientId
          });
          userId = id;
        }

        const data = await chatService.getChatRoomWithMembers([
          userId,
          params.meId
        ]);
        return { data };
      },
      invalidatesTags: (res, err, args) =>
        res.members.map(({ id }) => ({ type: ChatApiTags.ChatRooms, id })) ?? []
    })
  })
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMessagesQuery,
  useGetChatQuery,
  useSendMessageMutation,
  useUpdateMessageMutation,
  useGetChatUnreadQuery,
  useGetActiveStatusQuery,
  useCreateChatRoomMutation,
  useUpdateMessagesToSeenMutation
} = chatApi;
